import { useCallback, useEffect, useState } from 'react'
import { ScansService, UserInputService } from '@core/services/api-gateway'
import { useIsIssueInProcess } from '@/core/hooks/useIsIssueInProcess'
import { generateToast } from '@components/toasting'
import { AppModule } from '@core/modules/app/app.module'
import { IntegrationsModule } from '@core/modules/integrations'
import { IssuesModule } from '@core/modules/issues'
import { getIssueAnalyticsProps } from '@core/modules/issues/analytics'
import { UserInputsModule } from '@core/modules/user-inputs'
import { Analytics } from '@core/services/mixpanel'
import {
    TResolveFailureReason,
    TResolveMode,
    TResolveSelection,
    UseIssueResolutionParams,
} from './useIssueResolution.types'
import { deriveResolveOptions } from './useIssueResolution.utils'

const RESOLVE_ERROR_MESSAGE = 'An error was occurred, please try again later'

const showResolveError = () =>
    generateToast({ type: 'error', data: RESOLVE_ERROR_MESSAGE })

export const useIssueResolution = ({
    issue,
    closeDrawer,
}: UseIssueResolutionParams) => {
    const [comment, setComment] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { isIssueInProcess } = useIsIssueInProcess(issue.id)
    const { scanInProgress } = AppModule.useStore()

    useEffect(() => {
        // Preload resolve options so the first submit doesn't wait on a fetch
        void UserInputsModule.loadFormData()
    }, [issue.id])

    const submit = useCallback(
        async (mode: TResolveMode, selection: TResolveSelection) => {
            const resolutionEventProps = {
                ...getIssueAnalyticsProps(issue),
                resolutionMode: mode,
                assetsCount: selection.assets?.length ?? 0,
                technologiesCount: selection.technologies?.length ?? 0,
            }

            const failResolve = (reason: TResolveFailureReason) => {
                showResolveError()
                Analytics.event('SEM::issue_resolve_failed', {
                    ...resolutionEventProps,
                    reason,
                })
            }

            Analytics.event(
                'SEM::users_clicks_mark_as_resolved',
                resolutionEventProps
            )

            const formData = await UserInputsModule.loadFormData().catch(
                () => false as const
            )
            if (!formData) {
                failResolve('form-data-unavailable')
                return
            }

            const { fixedAction, notAnIssueAction } =
                deriveResolveOptions(formData)
            const action = mode === 'fixed' ? fixedAction : notAnIssueAction
            if (!action) {
                failResolve('resolve-option-missing')
                return
            }

            const isFindingIssue =
                issue.issue_type === 'FRAUD_DEFENSE' ||
                issue.issue_type === 'INBOX_RULE'

            IssuesModule.addInProcessIssueId(issue.id)
            setIsSubmitting(true)

            const result = await UserInputService.markIssueAsResolved({
                issueId: isFindingIssue ? issue.finding_id : issue.id,
                action,
                assets: (selection.assets ?? []) as unknown as TAsset[],
                technologies: selection.technologies ?? [],
                resolution_text: comment,
            })
            setIsSubmitting(false)

            if (!result) {
                failResolve('request-failed')
                IssuesModule.removeInProcessIssueId(issue.id)
                return
            }

            Analytics.event('SEM::user_resolved_an_issue', {
                ...resolutionEventProps,
                resolutionText: comment,
                resolutionTextLength: comment.length,
            })

            closeDrawer()
            ScansService.getScans(3000).catch(console.error)
            IntegrationsModule.pollIntegrationsScans()
            await UserInputsModule.pollProcesses({ resolvedIssueId: issue.id })
        },
        [issue, comment, closeDrawer]
    )

    const submitFixed = useCallback(
        (selection: TResolveSelection) => submit('fixed', selection),
        [submit]
    )

    const submitNotAnIssue = useCallback(
        (selection: TResolveSelection) => submit('notAnIssue', selection),
        [submit]
    )

    return {
        comment,
        setComment,
        submitFixed,
        submitNotAnIssue,
        scanInProgress,
        loading: isSubmitting || scanInProgress || isIssueInProcess,
    }
}
